import { useState } from 'react';
import { toast } from 'sonner';

import { productionApi } from 'api/production';
import { CONTENT_VARIANTS, TOAST_ERROR_STYLE } from 'components/production/components/AisleContent/constants';
import { useProductTableHandlers } from 'components/production/components/AisleContent/hooks/useProductTableHandlers';
import { useQueryParams } from 'components/production/components/AisleContent/hooks/useQueryParams';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';

import CarouselEditorActions from './CarouselEditorActions';
import CarouselEditorForm from './CarouselEditorForm';
import CarouselEditorSummary from './CarouselEditorSummary';

/**
 * Displays an editor for carousels.
 *
 * @param {Object} aisleID - Aisle ID.
 * @param {Object} aisleLevel - Aisle level.
 * @param {Object} carousel - Carousel data.
 * @param {Object} index - Index of the carousel.
 * @param {string} initialView - Initial view, defaults to 'summary'.
 * @param {Function} [handleDeleteNewEditor] - Callback function to delete the new carousel editor.
 * @param {Function} refetchCarousels - Function to refetch carousels from the parent component.
 * @param {Object} sellableItems - Sellable items.
 * @param {Array} sortedAisles - The list of aisles to duplicate to.
 */
export default function CarouselEditor({
    aisleID,
    aisleLevel,
    carousel,
    handleDeleteNewEditor,
    index,
    initialView = 'summary',
    refetchCarousels,
    sellableItems,
    sortedAisles,
}) {
    const { queryParamsData } = useQueryParams();

    const [view, setView] = useState(initialView);
    const [imageError, setImageError] = useState(null);
    const [backgroundImageError, setBackgroundImageError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // * The GET /carousels endpoint returns `linkTitle`, but the PUT/POST endpoints expect `linkText`.
    // * The GET /carousels endpoint returns `photoUrlMedium`, but the PUT/POST endpoints expect `imageUrl`.
    const [formData, setFormData] = useState({
        ...carousel,
        ...(carousel.photoUrlMedium ? { imageUrl: carousel.photoUrlMedium } : {}),
        ...(carousel.linkTitle ? { linkText: carousel.linkTitle } : {}),
    });

    const productTableHandlers = useProductTableHandlers(formData.items, setFormData, sellableItems);

    const toggleView = () => {
        setView((prev) => (prev === 'summary' ? 'form' : 'summary'));
    };

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleImageClear = () => {
        setFormData((prev) => ({ ...prev, imageUrl: '' }));
    };

    const handleBackgroundImageClear = () => {
        setFormData((prev) => ({ ...prev, backgroundImageUrl: null }));
    };

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            setFormData((prev) => ({ ...prev, imageUrl }));
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleBackgroundImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file, true);
            const backgroundImageUrl = response.data.imagePath;
            setFormData((prev) => ({ ...prev, backgroundImageUrl }));
            setBackgroundImageError(null);
        } catch (e) {
            setBackgroundImageError(`Error: ${e.message}`);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const loadingToastID = toast.loading('Saving carousel...');

        // rankNumber is not a valid field for the PUT/POST endpoints
        // but it may be returned by the BE in GET requests, so removing it here.
        delete formData.rankNumber;

        try {
            await productionApi.createUpdateCarouselV2({
                packGroupID: queryParamsData.packGroupID,
                carousels: [
                    {
                        ...formData,
                        ...(carousel.id ? { carouselID: carousel.id } : {}),
                        [aisleLevel === 1 ? 'l1AisleID' : 'l2AisleID']: aisleID,
                        featured: false,
                        type: 'NORMAL',
                        // The GET /carousels endpoint returns items with the `id` set to `carousel_sellable_item_map.id`,
                        // but the PUT/POST endpoints expect the `id` to be the `sellable_item.id`.
                        items: formData.items.map((item) => ({
                            ...item,
                            id: item.sellableItemID ? item.sellableItemID : item.id,
                        })),
                    },
                ],
            });
            toast.success('Carousel saved successfully');
            handleDeleteNewEditor?.();
        } catch (error) {
            console.error(error);
            toast.error('Failed to save carousel', {
                style: TOAST_ERROR_STYLE,
            });
        } finally {
            setIsSaving(false);
            toast.dismiss(loadingToastID);
            refetchCarousels();
        }
    };

    const handleDelete = async ({ carouselID }) => {
        if (!carouselID) {
            handleDeleteNewEditor();
            return;
        }

        setIsSaving(true);
        const loadingToastID = toast.loading('Deleting carousel...');

        try {
            const response = await productionApi.deleteCarousel(carouselID);
            if (!response.status) {
                throw new Error('Failed to delete carousel');
            }
            toast.success('Carousel deleted successfully');
        } catch (error) {
            console.error(error);
            toast.error(error.message, {
                style: TOAST_ERROR_STYLE,
            });
        } finally {
            setIsSaving(false);
            toast.dismiss(loadingToastID);
            refetchCarousels();
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    {carousel.name ? (
                        <div className="flex items-center gap-1">
                            <h3>{carousel.name}</h3>
                            <span className="ml-2 text-sm text-muted-foreground">
                                ({CONTENT_VARIANTS[carousel.displayType]?.name})
                            </span>
                            <span className="ml-2 font-normal text-sm text-muted-foreground">id: {carousel.id}</span>
                        </div>
                    ) : (
                        <h3>{CONTENT_VARIANTS[carousel.displayType]?.name}</h3>
                    )}
                    <CarouselEditorActions
                        carousel={carousel}
                        onDelete={() => handleDelete({ carouselID: carousel.id })}
                        refetchCarousels={refetchCarousels}
                        sortedAisles={sortedAisles}
                        toggleView={toggleView}
                        view={view}
                    />
                </CardTitle>
            </CardHeader>
            <CardContent>
                {view === 'summary' && (
                    <CarouselEditorSummary data={carousel} formData={formData} toggleView={toggleView} />
                )}
                {view === 'form' && (
                    <CarouselEditorForm
                        data={carousel}
                        formData={formData}
                        backgroundImageError={backgroundImageError}
                        handleBackgroundImageClear={handleBackgroundImageClear}
                        handleBackgroundImageUpload={handleBackgroundImageUpload}
                        handleChange={handleChange}
                        handleImageClear={handleImageClear}
                        handleImageUpload={handleImageUpload}
                        handleSave={handleSave}
                        imageError={imageError}
                        index={index}
                        productTableHandlers={productTableHandlers}
                        isSaving={isSaving}
                        sellableItems={sellableItems}
                    />
                )}
            </CardContent>
        </Card>
    );
}
